<template>
  <div class="lang-toggle" aria-label="Language">
    <!-- a11y: the checkbox is the control, label is the track -->
    <input
      id="lang-toggle"
      type="checkbox"
      :checked="isEn"
      @change="onToggle"
      aria-label="Toggle language"
    />
    <label for="lang-toggle">
      <span class="label-es">ES</span>
      <span class="label-en">EN</span>
      <!-- knob is drawn via ::after for best perf -->
    </label>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { setLocale } from '@/i18n';
import { computed } from 'vue';

export default {
  name: 'LangSwitcher',
  setup() {
    const { locale } = useI18n();
    const isEn = computed(() => (locale.value || 'en').toLowerCase().startsWith('en'));

    const onToggle = (e) => {
      const to = e.target.checked ? 'en' : 'es';
      setLocale(to);
    };

    return { isEn, onToggle };
  }
};
</script>

<style scoped>
/* Container keeps a little safe-area breathing room */
.lang-toggle {
  position: relative;
  display: inline-block;
  padding-right: max(0px, env(safe-area-inset-right));
  /* Prevent accidental clipping of glow/shadow */
  overflow: visible;
}

/* Hide the native checkbox */
.lang-toggle input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
}

/* === Track (pill) === */
.lang-toggle label {
  --accent: var(--accent, #3EBEA9);
  --accent-500: var(--accent-500, #3EBEA9);
  --accent-600: var(--accent-600, #2fa390);
  --accent-700: var(--accent-700, #1e7862);
  
  /* Adaptive colors - defaults for light backgrounds */
  --adaptive-bg: var(--lang-toggle-bg, #ffffff);
  --adaptive-bg-subtle: var(--lang-toggle-bg-subtle, #f8fafc);
  --adaptive-border: var(--lang-toggle-border, rgba(0,0,0,.08));
  --adaptive-border-hover: var(--lang-toggle-border-hover, rgba(0,0,0,.15));
  --adaptive-text-muted: var(--lang-toggle-text-muted, #64748b);
  --adaptive-text-active: var(--lang-toggle-text-active, #0f172a);
  --adaptive-shadow-light: var(--lang-toggle-shadow-light, rgba(0, 0, 0, 0.04));
  --adaptive-shadow-medium: var(--lang-toggle-shadow-medium, rgba(0, 0, 0, 0.06));
  --adaptive-text-shadow-active: var(--lang-toggle-text-shadow-active, rgba(255, 255, 255, 0.8));

  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;

  width: 84px;
  height: 40px;
  padding: 3px;
  border: 1.5px solid var(--adaptive-border);
  border-radius: 24px;
  background: var(--adaptive-bg);
  cursor: pointer;
  user-select: none;

  /* Adaptive glass effect */
  box-shadow: 
    0 1px 2px var(--adaptive-shadow-light),
    0 2px 8px var(--adaptive-shadow-medium),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  
  transition:
    border-color .25s cubic-bezier(.4,0,.2,1),
    background-color .25s cubic-bezier(.4,0,.2,1),
    box-shadow .25s cubic-bezier(.4,0,.2,1),
    transform .12s cubic-bezier(.4,0,.2,1);
}

/* Hover state - adaptive */
.lang-toggle label:hover {
  border-color: var(--adaptive-border-hover);
  background: var(--adaptive-bg-subtle);
  box-shadow: 
    0 2px 4px var(--adaptive-shadow-medium),
    0 4px 16px var(--adaptive-shadow-light),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

/* Press feedback - adaptive */
.lang-toggle label:active {
  transform: scale(0.98);
  box-shadow: 
    0 1px 2px var(--adaptive-shadow-light),
    0 2px 8px var(--adaptive-shadow-medium),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

/* Labels (ES/EN) inside the track */
.label-es, .label-en {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.25px;
  line-height: 1;
  text-align: center;
  z-index: 2;
  color: var(--adaptive-text-muted);
  transition: 
    color .25s cubic-bezier(.4,0,.2,1), 
    font-weight .25s cubic-bezier(.4,0,.2,1),
    text-shadow .25s ease;
  position: relative;
}

/* Knob (circle) - better proportions and effects */
.lang-toggle label::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, 
    var(--accent-500) 0%, 
    var(--accent-600) 70%, 
    var(--accent-700) 100%);
  
  /* Premium shadow with glow */
  box-shadow:
    0 2px 4px rgba(0,0,0,.08),
    0 4px 12px rgba(62, 190, 169, .2),
    0 1px 2px rgba(0,0,0,.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1);
  
  will-change: transform;
  z-index: 1;
  
  /* Smooth, spring-like transition */
  transition: 
    transform .3s cubic-bezier(.34,1.26,.64,1),
    background .25s cubic-bezier(.4,0,.2,1),
    box-shadow .25s cubic-bezier(.4,0,.2,1);
}

/* Checked state (EN on) - adaptive background */
.lang-toggle input:checked + label {
  border-color: color-mix(in srgb, var(--accent) 30%, var(--adaptive-border));
  background: color-mix(in srgb, var(--accent) 6%, var(--adaptive-bg));
  
  box-shadow: 
    0 2px 4px var(--adaptive-shadow-medium),
    0 4px 16px rgba(62, 190, 169, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

/* Move knob to the right when checked - better positioning */
.lang-toggle input:checked + label::after {
  transform: translateX(44px);
  background: linear-gradient(135deg, 
    var(--accent-600) 0%, 
    var(--accent-700) 70%, 
    color-mix(in srgb, var(--accent-700) 90%, black) 100%);
  box-shadow:
    0 3px 6px rgba(0,0,0,.1),
    0 6px 16px rgba(47, 163, 144, .25),
    0 1px 3px rgba(0,0,0,.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.25),
    inset 0 -1px 0 rgba(0, 0, 0, 0.15);
}

/* Highlight the active side's text - adaptive contrast */
.lang-toggle input:not(:checked) + label .label-es { 
  color: var(--adaptive-text-active); 
  font-weight: 700;
  text-shadow: 0 1px 2px var(--adaptive-text-shadow-active);
}

.lang-toggle input:checked + label .label-en { 
  color: var(--adaptive-text-active); 
  font-weight: 700;
  text-shadow: 0 1px 2px var(--adaptive-text-shadow-active);
}

/* Focus styles for accessibility - more visible */
.lang-toggle input:focus-visible + label {
  outline: 2px solid var(--accent);
  outline-offset: 3px;
  border-color: var(--accent);
}

/* === ADAPTIVE COLOR SYSTEM === */
/* Default: Light background (scrolled state) */
.lang-toggle {
  --lang-toggle-bg: #ffffff;
  --lang-toggle-bg-subtle: #f8fafc;
  --lang-toggle-border: rgba(0,0,0,.08);
  --lang-toggle-border-hover: rgba(0,0,0,.15);
  --lang-toggle-text-muted: #64748b;
  --lang-toggle-text-active: #0f172a;
  --lang-toggle-shadow-light: rgba(0, 0, 0, 0.04);
  --lang-toggle-shadow-medium: rgba(0, 0, 0, 0.06);
  --lang-toggle-text-shadow-active: rgba(255, 255, 255, 0.8);
}

/* Dark background (top section) - add this class when header is dark */
.header-dark .lang-toggle {
  --lang-toggle-bg: rgba(255, 255, 255, 0.1);
  --lang-toggle-bg-subtle: rgba(255, 255, 255, 0.15);
  --lang-toggle-border: rgba(255,255,255,.2);
  --lang-toggle-border-hover: rgba(255,255,255,.3);
  --lang-toggle-text-muted: rgba(255, 255, 255, 0.7);
  --lang-toggle-text-active: #ffffff;
  --lang-toggle-shadow-light: rgba(0, 0, 0, 0.1);
  --lang-toggle-shadow-medium: rgba(0, 0, 0, 0.2);
  --lang-toggle-text-shadow-active: rgba(0, 0, 0, 0.3);
}

/* Add backdrop blur for glass effect on dark background */
.header-dark .lang-toggle label {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

/* System dark mode fallback - only if no specific theme class */
@media (prefers-color-scheme: dark) {
  .lang-toggle:not(.header-light) {
    --lang-toggle-bg: rgba(255, 255, 255, 0.08);
    --lang-toggle-bg-subtle: rgba(255, 255, 255, 0.12);
    --lang-toggle-border: rgba(255,255,255,.15);
    --lang-toggle-border-hover: rgba(255,255,255,.25);
    --lang-toggle-text-muted: #94a3b8;
    --lang-toggle-text-active: #f1f5f9;
    --lang-toggle-shadow-light: rgba(0, 0, 0, 0.2);
    --lang-toggle-shadow-medium: rgba(0, 0, 0, 0.3);
    --lang-toggle-text-shadow-active: rgba(0, 0, 0, 0.4);
  }
}

/* Reduce motion for accessibility */
@media (prefers-reduced-motion: reduce) {
  .lang-toggle label,
  .lang-toggle label::after,
  .label-es, .label-en {
    transition: none;
  }
}

/* Improved mobile scaling */
@media (max-width: 480px) {
  .lang-toggle label { 
    width: 76px; 
    height: 36px; 
    padding: 2px;
  }
  .lang-toggle label::after { 
    width: 32px; 
    height: 32px; 
    top: 2px; 
    left: 2px; 
  }
  .lang-toggle input:checked + label::after { 
    transform: translateX(40px); 
  }
  .label-es, .label-en { 
    font-size: 12px; 
  }
}

/* Ultra-compact for very small screens */
@media (max-width: 360px) {
  .lang-toggle label { 
    width: 70px; 
    height: 32px; 
    padding: 2px;
  }
  .lang-toggle label::after { 
    width: 28px; 
    height: 28px; 
  }
  .lang-toggle input:checked + label::after { 
    transform: translateX(36px); 
  }
  .label-es, .label-en { 
    font-size: 11px; 
    font-weight: 600;
  }
}</style>